// Color palettes per pack. 10 swatches each per spec.
//
// Joseph: warm earth + accent cool — covers skin, sand, sun, robe colors.
// Nativity: cool night + warm lamp — sky, snow, gold, fire, manger wood.
//
// Picked so that any single swatch is recognizably distinct on a small
// touch palette (Lab distance > 25 between adjacent swatches).

import type { RGB } from './types.ts'

export interface Swatch {
  hex: `#${string}`
  rgb: RGB
  /** Display name in zh-Hant for accessibility / tooltips. */
  nameZh: string
}

function rgb(hex: `#${string}`): RGB {
  const h = hex.slice(1)
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

function s(hex: `#${string}`, nameZh: string): Swatch {
  return { hex, rgb: rgb(hex), nameZh }
}

export const JOSEPH_PALETTE: readonly Swatch[] = [
  s('#c9722d', '土橙'),
  s('#f5d68a', '麥金'),
  s('#7a3b1a', '深棕'),
  s('#e8b074', '駝色'),
  s('#a01515', '朱紅'),
  s('#1f3a5f', '夜藍'),
  s('#5a8a3a', '橄欖'),
  s('#fff6e3', '米白'),
  s('#3a2a18', '咖啡'),
  s('#d4a017', '純金'),
] as const

export const NATIVITY_PALETTE: readonly Swatch[] = [
  s('#1f3a5f', '夜藍'),
  s('#0d1f3a', '午夜'),
  s('#f5c542', '燈金'),
  s('#fff6e3', '雪白'),
  s('#a06a3a', '榖倉'),
  s('#7a8e9b', '銀灰'),
  s('#c2855e', '皮革'),
  s('#e89455', '燭橘'),
  s('#3d5a3d', '松綠'),
  s('#92314d', '酒紅'),
] as const

// Cross-pack general palette — opened from "更多色" tab on ColoringPage. 24 swatches:
// 7 hues × 3 shades (light/mid/dark) = 21, plus black/mid-gray/white = 24. Skin and
// brown are intentionally NOT duplicated here — they live in JOSEPH_PALETTE (駝色,
// 深棕, 咖啡) and NATIVITY_PALETTE (皮革). If alpha dogfood shows kids need them in
// the general tab too, add them then.
export const GENERAL_PALETTE: readonly Swatch[] = [
  s('#ffe0e0', '粉紅'),
  s('#ff6b6b', '西瓜紅'),
  s('#a30015', '暗紅'),
  s('#ffd9b3', '杏橙'),
  s('#ff9933', '橘色'),
  s('#cc5500', '焦橙'),
  s('#fff5b3', '鵝黃'),
  s('#ffd633', '鮮黃'),
  s('#bf9000', '黃褐'),
  s('#c2eccb', '嫩綠'),
  s('#42b85e', '草綠'),
  s('#1f6b2e', '墨綠'),
  s('#b3eaef', '水藍'),
  s('#3ecbd6', '湖青'),
  s('#0e6e78', '孔雀青'),
  s('#cfd9ff', '天藍'),
  s('#4a7df0', '群青'),
  s('#1a3a8a', '深藍'),
  s('#e5d4ff', '薰衣紫'),
  s('#9966cc', '葡紫'),
  s('#522d80', '暗紫'),
  s('#000000', '黑'),
  s('#9aa0a6', '中灰'),
  s('#ffffff', '白'),
] as const

export const PALETTES = {
  joseph: JOSEPH_PALETTE,
  nativity: NATIVITY_PALETTE,
  general: GENERAL_PALETTE,
} as const

export type PaletteId = keyof typeof PALETTES

export function getPalette(paletteId: PaletteId): readonly Swatch[] {
  return PALETTES[paletteId]
}
